import { Ionicons } from '@expo/vector-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

type Country = { flag: string; nameKey: string; dial: string };

const COUNTRIES: Country[] = [
  { flag: '🇬🇷', nameKey: 'phoneInput.country_gr', dial: '+30' },
  { flag: '🇨🇾', nameKey: 'phoneInput.country_cy', dial: '+357' },
  { flag: '🇦🇱', nameKey: 'phoneInput.country_al', dial: '+355' },
  { flag: '🇧🇬', nameKey: 'phoneInput.country_bg', dial: '+359' },
  { flag: '🇷🇴', nameKey: 'phoneInput.country_ro', dial: '+40' },
  { flag: '🇩🇪', nameKey: 'phoneInput.country_de', dial: '+49' },
  { flag: '🇬🇧', nameKey: 'phoneInput.country_gb', dial: '+44' },
  { flag: '🇺🇸', nameKey: 'phoneInput.country_us', dial: '+1' },
  { flag: '🇫🇷', nameKey: 'phoneInput.country_fr', dial: '+33' },
  { flag: '🇮🇹', nameKey: 'phoneInput.country_it', dial: '+39' },
  { flag: '🇪🇸', nameKey: 'phoneInput.country_es', dial: '+34' },
  { flag: '🇵🇹', nameKey: 'phoneInput.country_pt', dial: '+351' },
  { flag: '🇳🇱', nameKey: 'phoneInput.country_nl', dial: '+31' },
  { flag: '🇧🇪', nameKey: 'phoneInput.country_be', dial: '+32' },
  { flag: '🇦🇹', nameKey: 'phoneInput.country_at', dial: '+43' },
  { flag: '🇨🇭', nameKey: 'phoneInput.country_ch', dial: '+41' },
  { flag: '🇸🇪', nameKey: 'phoneInput.country_se', dial: '+46' },
  { flag: '🇩🇰', nameKey: 'phoneInput.country_dk', dial: '+45' },
  { flag: '🇳🇴', nameKey: 'phoneInput.country_no', dial: '+47' },
  { flag: '🇦🇺', nameKey: 'phoneInput.country_au', dial: '+61' },
];

type Props = {
  value: string;
  onChangeText: (national: string) => void;
  onChangeDialCode: (dial: string) => void;
  dialCode: string;
  error?: string;
};

export function PhoneInput({ value, onChangeText, dialCode, onChangeDialCode, error }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [open, setOpen] = useState(false);
  const insets = useSafeAreaInsets();

  const selected = COUNTRIES.find((c) => c.dial === dialCode) ?? COUNTRIES[0];

  return (
    <>
      <View style={[styles.row, !!error && styles.rowError]}>
        <Pressable style={styles.dialBtn} onPress={() => setOpen(true)} accessibilityRole="button">
          <Text style={styles.flag}>{selected.flag}</Text>
          <Text style={styles.dial}>{dialCode}</Text>
          <Ionicons name="chevron-down" size={14} color={colors.textMuted} />
        </Pressable>
        <View style={styles.divider} />
        <TextInput
          style={styles.input}
          value={value}
          onChangeText={onChangeText}
          placeholder={t('placeholder.phoneNumber')}
          placeholderTextColor={colors.textMuted}
          keyboardType="phone-pad"
          autoComplete="tel"
          textContentType="telephoneNumber"
        />
      </View>
      {!!error && <Text style={styles.errorText}>{error}</Text>}

      <Modal visible={open} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setOpen(false)}>
        <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.lg }]}>
          <View style={styles.sheetHeader}>
            <Text style={styles.sheetTitle}>{t('common.selectCountry')}</Text>
            <Pressable onPress={() => setOpen(false)} hitSlop={12}>
              <Ionicons name="close" size={24} color={colors.text} />
            </Pressable>
          </View>
          <FlatList
            data={COUNTRIES}
            keyExtractor={(c) => c.dial + c.nameKey}
            renderItem={({ item }) => (
              <Pressable
                style={[styles.countryRow, item.dial === dialCode && styles.countryRowSelected]}
                onPress={() => {
                  onChangeDialCode(item.dial);
                  setOpen(false);
                }}
              >
                <Text style={styles.countryFlag}>{item.flag}</Text>
                <Text style={styles.countryName}>{t(item.nameKey)}</Text>
                <Text style={styles.countryDial}>{item.dial}</Text>
                {item.dial === dialCode && (
                  <Ionicons name="checkmark" size={18} color={colors.primary} />
                )}
              </Pressable>
            )}
            ItemSeparatorComponent={() => <View style={styles.sep} />}
          />
        </View>
      </Modal>
    </>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      height: 48,
      overflow: 'hidden',
    },
    rowError: {
      borderColor: colors.danger,
    },
    dialBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: spacing.sm,
      height: '100%',
    },
    flag: {
      fontSize: 20,
    },
    dial: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    divider: {
      width: 1,
      height: 28,
      backgroundColor: colors.border,
    },
    input: {
      flex: 1,
      paddingHorizontal: spacing.sm,
      color: colors.text,
      fontSize: fontSize.md,
      height: '100%',
    },
    errorText: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
    sheet: {
      flex: 1,
      backgroundColor: colors.background,
    },
    sheetHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    sheetTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    countryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
    },
    countryRowSelected: {
      backgroundColor: colors.surfaceAlt,
    },
    countryFlag: {
      fontSize: 24,
    },
    countryName: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.md,
    },
    countryDial: {
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    sep: {
      height: 1,
      backgroundColor: colors.border,
      marginLeft: spacing.md,
    },
  });
}
